"use client";

import posthog from "posthog-js";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, type ReactNode, useEffect } from "react";

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://t.reelmagicapp.com";
const isVercelProduction =
  process.env.NEXT_PUBLIC_VERCEL_ENV === "production";
const shouldEnablePostHog = Boolean(posthogKey) && isVercelProduction;

function PostHogPageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!shouldEnablePostHog || !pathname) {
      return;
    }

    const query = searchParams.toString();
    const pathWithQuery = query ? `${pathname}?${query}` : pathname;

    posthog.capture("$pageview", {
      $current_url: window.location.href,
      path: pathWithQuery,
    });
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (!posthogKey || !shouldEnablePostHog) {
      return;
    }

    posthog.init(posthogKey, {
      api_host: posthogHost,
      defaults: "2026-05-30",
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
    });
  }, []);

  if (!shouldEnablePostHog) {
    return <>{children}</>;
  }

  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageViewTracker />
      </Suspense>
      {children}
    </>
  );
}
